import type { ApprovalSecurityLevel } from '@/types/approval';
import { REFERRER_RESTRICTED_SECURITY } from './referrerAddable';

/**
 * 공개레벨(security) 코드 ↔ 작성폼 상태(secuLevel 라디오 + restrictReferrer 체크) 변환.
 *
 * 서버 코드는 1/5/7/9 네 가지지만 라디오는 1/5/7 만 노출한다.
 * 9(비공개)는 별도 항목이 아니라 7(결재라인·참조인 공개) + restrictReferrer 체크 ON 으로 표현한다.
 * 신규 작성(양식의 defaultSecurityLevel)과 편집(문서의 security)이 같은 규칙을 쓰도록
 * 인코딩/디코딩을 이 모듈 한 곳에 모아둔다.
 */

/** 1 = 모든 임직원 공개. */
export const SECU_LEVEL_ALL = '1';
/** 5 = 상신자와 결재라인 소속 부서 공개. */
export const SECU_LEVEL_DEPARTMENT = '5';
/** 7 = 결재라인 · 참조인 공개. 9(비공개)도 이 값 + restrictReferrer 체크로 표현한다. */
export const SECU_LEVEL_APPROVAL_LINE = '7';

/**
 * 라디오에 노출하는 값 — 9 는 별도 항목이 아니라 7 + 체크로 표현하므로 제외.
 * (SecuLevelSection 의 옵션 순서와 동일)
 */
const RADIO_SECU_LEVELS = new Set([
  SECU_LEVEL_ALL,
  SECU_LEVEL_DEPARTMENT,
  SECU_LEVEL_APPROVAL_LINE,
]);

/** 서버가 받는 공개레벨 코드 전체. 라디오 3종 + 9(비공개). */
const SECURITY_LEVELS = new Set<number>([
  ...[...RADIO_SECU_LEVELS].map(Number),
  REFERRER_RESTRICTED_SECURITY,
]);

/**
 * 값이 없을 때(양식에 기본값 미설정 / 문서 security null) 쓰는 값.
 * 폼 defaultValues 와 동일하게 1(모든 임직원 공개).
 */
const SECU_LEVEL_WHEN_ABSENT = SECU_LEVEL_ALL;

/**
 * 라디오에 없는 미지의 코드가 내려왔을 때의 폴백.
 * 어떤 라디오에도 매칭되지 않아 전부 미선택으로 보이는 상태를 만들지 않되,
 * 라디오 중 공개 범위가 가장 좁은 7(결재라인·참조인)로 떨어뜨린다.
 * 미지의 코드는 데이터 이상 상황이고, 그대로 저장하면 표시된 값이 그대로 기록되므로
 * 의도보다 넓게 공개되는 쪽으로 틀어지지 않는 방향을 택한다.
 * (양식 메타 215건·문서 250건 표본 모두 1/5/7/9 뿐이라 실제로 타지 않는 방어 경로다.)
 */
const SECU_LEVEL_FALLBACK = SECU_LEVEL_APPROVAL_LINE;

export type SecuLevelFormState = {
  secuLevel: string;
  restrictReferrer: boolean;
};

/**
 * 서버 코드 → 폼 상태.
 * - null/undefined: 1 (폼 기본값과 동일)
 * - 9: 7 + restrictReferrer 체크
 * - 1/5/7: 그대로
 * - 그 외: 7 로 폴백 (SECU_LEVEL_FALLBACK 참고)
 */
export const decodeSecurityLevel = (
  security?: number | null,
): SecuLevelFormState => {
  if (security == null) {
    return { secuLevel: SECU_LEVEL_WHEN_ABSENT, restrictReferrer: false };
  }
  if (security === REFERRER_RESTRICTED_SECURITY) {
    return { secuLevel: SECU_LEVEL_APPROVAL_LINE, restrictReferrer: true };
  }
  const value = String(security);
  return {
    secuLevel: RADIO_SECU_LEVELS.has(value) ? value : SECU_LEVEL_FALLBACK,
    restrictReferrer: false,
  };
};

const parseSecurityLevel = (v: string): ApprovalSecurityLevel | null => {
  const n = Number(v);
  return SECURITY_LEVELS.has(n) ? (n as ApprovalSecurityLevel) : null;
};

/**
 * 폼 상태 → 서버 코드. 7 + restrictReferrer 체크는 9(비공개)로 인코딩한다.
 * decodeSecurityLevel 의 역변환이라 9 로 들어온 값은 저장 시 다시 9 가 된다.
 */
export const encodeSecurityLevel = (
  secuLevel: string,
  restrictReferrer: boolean,
): ApprovalSecurityLevel | null => {
  if (secuLevel === SECU_LEVEL_APPROVAL_LINE && restrictReferrer) {
    return REFERRER_RESTRICTED_SECURITY;
  }
  return parseSecurityLevel(secuLevel);
};
